import { supabase } from '../lib/supabase'

/**
 * The access token that makes a client link work.
 *
 * Read only here, only for building the link a staff member copies. It is
 * never held in app state, rendered on its own, or used anywhere else.
 */

const cache = new Map<string, string>()

/**
 * The token on the project's own row. Looked up by slug, falling back to the
 * project id for a project created moments ago whose slug lookup has not yet
 * settled. Returns '' only when the row genuinely cannot be read — the caller
 * must not build a link without a token.
 */
export async function fetchAccessToken(slug: string, projectId = ''): Promise<string> {
  if (!supabase) return ''

  const cached = cache.get(slug)
  if (cached) return cached

  const token = (await lookup('slug', slug)) || (await lookup('id', projectId))
  if (token) cache.set(slug, token)
  return token
}

async function lookup(column: 'slug' | 'id', value: string): Promise<string> {
  if (!supabase || !value) return ''

  const { data, error } = await supabase
    .from('projects')
    .select('access_token')
    .eq(column, value)
    .maybeSingle()

  if (error) {
    console.warn(`[supabase] access token lookup by ${column} failed: ${error.message}`)
    return ''
  }
  return (data?.access_token as string | undefined) ?? ''
}
